import React, { useRef, forwardRef } from "react";
import { useReactToPrint } from "react-to-print";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import Logo from "../assets/logo.png";

const Invoice = forwardRef(
  ({ memberData, invoiceType, gstDetails, contactsNumbers }, ref) => {
    const componentRef = useRef();

    const formattedString = contactsNumbers[0] + " / " + contactsNumbers[1];

    const handlePrint = useReactToPrint({
      content: () => componentRef.current,
    });

    const handleDownloadPdf = () => {
      const input = componentRef.current;

      html2canvas(input).then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const imgWidth = 210; // A4 width in mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
        pdf.save("invoice.pdf");
      });
    };

    const billingAmount = memberData
      ? parseFloat(memberData.billingAmount)
      : 21000;
    const sgst = (billingAmount * gstDetails.gstPercentage) / 2 / 100;
    const cgst = (billingAmount * gstDetails.gstPercentage) / 2 / 100;
    const totalAmount = billingAmount - (sgst + cgst);
    const balanceDue = 0;

    const invoiceData = {
      companyName: gstDetails?.companyName,
      companyAddress: gstDetails?.billingAddress,
      companyInstagram: "c3fitnesssciencekilpauk",
      companyGST: gstDetails?.gstNumber,
      memberName: memberData?.fullName,
      memberId: memberData?.memberId,
      membershipFee: billingAmount.toFixed(2),
      totalAmount: totalAmount.toFixed(2),
      sgst: sgst.toFixed(2),
      cgst: cgst.toFixed(2),
      grossTotal: billingAmount.toFixed(2),
      balanceDue: balanceDue.toFixed(2),
    };

    React.useImperativeHandle(ref, () => ({
      handlePrint,
      handleDownloadPdf,
    }));

    const getRenewalType = (startDate, expiryDate) => {
      if (!startDate || !expiryDate) return "Annual Renewal";

      const start = new Date(startDate);
      const expiry = new Date(expiryDate);

      const diffInMonths =
        (expiry.getFullYear() - start.getFullYear()) * 12 +
        (expiry.getMonth() - start.getMonth());

      if (diffInMonths <= 1) return "1 Month Renewal";
      if (diffInMonths <= 2) return "2 Month Renewal";
      if (diffInMonths <= 3) return "Quarterly Renewal";
      if (diffInMonths <= 6) return "Half-Yearly Renewal";
      return "Annual Renewal";
    };

    const renewalType = getRenewalType(
      memberData?.membershipStartDate,
      memberData?.renewalExpiryDate
    );

    function formatDate4(isoString) {
      const [year, month, day] = isoString.split("T")[0].split("-");
      return `${day}/${month}/${year}`;
    }

    return (
      <div>
        <div
          ref={componentRef}
          style={{
            padding: "20px",
            border: "1px solid #000",
            width: "80%",
            margin: "auto",
            marginTop: 40,
          }}
        >
          <div
            style={{
              display: "flex",
              marginBottom: 20,
              justifyContent: "center",
              columnGap: 40,
            }}
          >
            <div>
              <img style={{ width: 200 }} src={Logo} alt="logo" />
            </div>
            <div>
              <h2
                style={{
                  textAlign: "left",
                  marginBottom: 10,
                  fontWeight: "bold",
                }}
              >
                {invoiceData.companyName}
              </h2>
              <p style={{ margin: "5px 0", fontWeight: "bold" }}>
                {invoiceData.companyAddress}
              </p>
              <p style={{ margin: "5px 0", fontWeight: "bold" }}>
                Ph:
                {contactsNumbers.map((number, index) => (
                  <span key={index}>
                    {number}
                    {index < contactsNumbers.length - 1 ? " / " : ""}
                  </span>
                ))}
              </p>
              <p style={{ margin: "5px 0", fontWeight: "bold" }}>
                Instagram: {invoiceData.companyInstagram}
              </p>
              <p style={{ margin: "5px 0", fontWeight: "bold" }}>
                GST: {invoiceData.companyGST}
              </p>
            </div>
          </div>

          <hr />
          <h2 style={{ textAlign: "center", margin: "10px 0" }}>Invoice</h2>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: 20,
            }}
          >
            <div>
              <p style={{ margin: "5px 0", fontWeight: "bold" }}>
                <strong>Member ID:</strong> {memberData.memberId}
              </p>
              <p style={{ margin: "5px 0", fontWeight: "bold" }}>
                <strong>Member Name:</strong> {memberData.fullName}
              </p>
              <p style={{ margin: "5px 0", fontWeight: "bold" }}>
                <strong>Phone No:</strong> +91 {memberData.phoneNo}
              </p>
              {invoiceType == "With GST" && (
                <p style={{ margin: "5px 0", fontWeight: "bold" }}>
                  <strong>Member GST:</strong> {memberData?.gstNumber || "N/A"}
                </p>
              )}
              <p style={{ margin: "5px 0", fontWeight: "bold" }}>
                <strong>Member Address:</strong> {memberData?.address}
              </p>
            </div>
            <div>
              <p style={{ margin: "5px 0", fontWeight: "bold" }}>
                <strong>Receipt Number:</strong> 0{memberData.memberId}
              </p>
              <p style={{ margin: "5px 0", fontWeight: "bold" }}>
                <strong>Billing Date:</strong>{" "}
                {formatDate4(memberData.billDate)}
              </p>
            </div>

            <div>
              <p style={{ margin: "5px 0", fontWeight: "bold" }}>
                <strong>Start Date:</strong>{" "}
                {formatDate4(memberData.membershipStartDate)}
              </p>
              <p style={{ margin: "5px 0", fontWeight: "bold" }}>
                <strong>End Date:</strong>{" "}
                {formatDate4(memberData.renewalExpiryDate)}
              </p>
            </div>
          </div>

          <table
            width="100%"
            border="1"
            cellPadding="5"
            style={{ borderCollapse: "collapse" }}
          >
            <thead>
              <tr>
                <th style={{ padding: 8 }}>Particulars</th>
                <th style={{ padding: 8 }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: "8px" }}>{renewalType}</td>
                <td style={{ padding: "8px" }}>{invoiceData.membershipFee}</td>
              </tr>
              <tr>
                <td style={{ padding: "8px" }}>Discount (-)</td>
                <td style={{ padding: "8px" }}>0</td>
              </tr>
              <tr>
                <td style={{ padding: "8px" }}>Total Amount</td>
                <td style={{ padding: "8px" }}>
                  {invoiceType == "With GST"
                    ? invoiceData.totalAmount
                    : invoiceData.grossTotal}
                </td>
              </tr>

              {invoiceType == "With GST" && (
                <tr>
                  <td style={{ padding: "8px" }}>
                    SGST@ {gstDetails.gstPercentage / 2}%
                  </td>
                  <td style={{ padding: "8px" }}>{invoiceData.sgst}</td>
                </tr>
              )}
              {invoiceType == "With GST" && (
                <tr>
                  <td style={{ padding: "8px" }}>
                    CGST@ {gstDetails.gstPercentage / 2}%
                  </td>
                  <td style={{ padding: "8px" }}>{invoiceData.cgst}</td>
                </tr>
              )}
              <tr>
                <td style={{ padding: "8px" }}>Gross Total</td>
                <td style={{ padding: "8px" }}>{invoiceData.grossTotal}</td>
              </tr>
            </tbody>
          </table>
          <h4 style={{ textAlign: "center", marginTop: "20px" }}>
            For any queries, feedback or suggestions, please contact the
            management
          </h4>
          <p
            style={{
              textAlign: "right",
              marginTop: "20px",
              fontSize: 18,
              fontWeight: "bold",
            }}
          >
            For C3 Fitness Science
          </p>
          <p style={{ textAlign: "right", marginTop: "100px" }}>
            Authorized Signatory
          </p>
          <p
            style={{
              textAlign: "center",
              marginTop: "40px",
              fontWeight: "bold",
            }}
          >
            Thank You! Have A Nice Day!
          </p>
        </div>
      </div>
    );
  }
);

export default Invoice;
